import type { OrgUnitTreeNode } from '@ukinfo/shared';
import { useMemo } from 'react';
import { useScope } from '../lib/scope';
import { cn } from './ui';

/**
 * Auswahl von Bereichen.
 *
 * Der Baum wird eingerueckt dargestellt, damit auch tiefe Strukturen
 * verstaendlich bleiben. Im UI wird bewusst von "Bereichen" gesprochen.
 */
export function OrgUnitPicker({
  value,
  onChange,
  multiple = true,
  disabledIds,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  multiple?: boolean;
  disabledIds?: string[];
}): JSX.Element {
  const { tree } = useScope();
  const disabled = useMemo(() => new Set(disabledIds ?? []), [disabledIds]);

  const toggle = (id: string): void => {
    if (!multiple) {
      onChange([id]);
      return;
    }
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  const renderNode = (node: OrgUnitTreeNode, depth: number): JSX.Element => (
    <div key={node.id}>
      <label
        className={cn(
          'flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50',
          disabled.has(node.id) && 'cursor-not-allowed opacity-50',
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        <input
          type={multiple ? 'checkbox' : 'radio'}
          checked={value.includes(node.id)}
          disabled={disabled.has(node.id)}
          onChange={() => toggle(node.id)}
          className="h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        <span className={cn(depth === 0 && 'font-medium')}>{node.name}</span>
      </label>
      {node.children.map((child) => renderNode(child, depth + 1))}
    </div>
  );

  if (tree.length === 0) {
    return <p className="text-sm text-slate-500">Keine Bereiche verfügbar.</p>;
  }

  return (
    <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-300 bg-white p-1">
      {tree.map((node) => renderNode(node, 0))}
    </div>
  );
}

/** Kompakte Einfachauswahl als Dropdown. */
export function OrgUnitSelect({
  value,
  onChange,
  id,
  allowEmpty = false,
  emptyLabel = 'Gesamtverein',
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  id?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
}): JSX.Element {
  const { units } = useScope();

  const options = useMemo(
    () =>
      [...units].sort((a, b) => {
        const pa = a.path.join('/');
        const pb = b.path.join('/');
        return pa.localeCompare(pb, 'de');
      }),
    [units],
  );

  return (
    <select
      id={id}
      className="input"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
    >
      {allowEmpty && <option value="">{emptyLabel}</option>}
      {options.map((unit) => (
        <option key={unit.id} value={unit.id}>
          {' '.repeat(unit.depth * 3)}
          {unit.depth > 0 ? '└ ' : ''}
          {unit.name}
        </option>
      ))}
    </select>
  );
}
